import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Phone, Mail, MapPin, Check, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";

const companies = [
  {
    id: "mak-spare",
    name: "MAK Heavy Equipment",
    tagline: "Machinery Spare Parts Trading",
    logo: "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/uv54fxjp_logo1.png",
    description: "MAK Heavy Equipment Machinery Spare Parts Trading provides a comprehensive range of genuine and quality aftermarket spare parts for all types of heavy machinery. Our extensive inventory and competitive pricing ensure your equipment stays operational.",
    services: [
      "Genuine OEM Parts",
      "Quality Aftermarket Parts",
      "Engine Components",
      "Hydraulic Systems",
      "Undercarriage Parts",
      "Electrical Components"
    ],
    stats: [
      { value: "10K+", label: "Parts in Stock" },
      { value: "50+", label: "Brands" },
      { value: "Same Day", label: "Delivery" }
    ],
    image: "https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg?auto=compress&cs=tinysrgb&w=800"
  },
  {
    id: "hayalogix",
    name: "HayalogiX",
    tagline: "Transport & Equipment Rental",
    logo: "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/w779unkv_logo2.png",
    description: "HayalogiX is the transport and logistics arm of MAK Group, specializing in the movement of heavy equipment and machinery across the region. With a modern fleet and experienced operators, we ensure safe and timely delivery of your valuable assets.",
    services: [
      "Heavy Equipment Transport",
      "Machinery Relocation",
      "Project Logistics",
      "Fleet Management",
      "Cross-Border Transport",
      "Warehousing Solutions"
    ],
    stats: [
      { value: "50+", label: "Vehicles" },
      { value: "200+", label: "Deliveries/Month" },
      { value: "15+", label: "Countries" }
    ],
    image: "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=800"
  },
  {
    id: "bright-perfect",
    name: "Bright Perfect Heavy Equipment",
    tagline: "Spare Parts Trading L.L.C",
    logo: "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/s4mz9022_logo3.png",
    description: "Bright Perfect Heavy Equipment is your trusted partner for premium construction and industrial machinery. We offer sales, rental, and comprehensive maintenance services for a wide range of heavy equipment, backed by expert technical support.",
    services: [
      "Equipment Sales",
      "Short & Long-term Rental",
      "Preventive Maintenance",
      "Emergency Repairs",
      "Operator Training",
      "Equipment Inspection"
    ],
    stats: [
      { value: "100+", label: "Equipment Units" },
      { value: "300+", label: "Clients" },
      { value: "24/7", label: "Support" }
    ],
    image: "https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=800"
  },
  {
    id: "hammer-mount",
    name: "HAMMER MOUNT",
    tagline: "General Contracting Services",
    logo: null,
    description: "HAMMER MOUNT General Contracting Services provides comprehensive construction and contracting solutions. We specialize in infrastructure projects, building construction, and industrial facility development with a commitment to quality and timely delivery.",
    services: [
      "General Contracting",
      "Building Construction",
      "Infrastructure Projects",
      "Industrial Facilities",
      "Project Management",
      "Site Development"
    ],
    stats: [
      { value: "50+", label: "Projects" },
      { value: "100+", label: "Clients" },
      { value: "15+", label: "Years" }
    ],
    image: "https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg?auto=compress&cs=tinysrgb&w=800"
  }
];

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Thank you! We'll get back to you within 24 hours.");
    setFormData({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            data-testid="contact-name"
            className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
            placeholder="John Doe"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            data-testid="contact-email"
            className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
            placeholder="john@company.com"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            data-testid="contact-phone"
            className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
            placeholder="+971 56 341 9000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            data-testid="contact-company"
            className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
            placeholder="Your Company"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Service Interested In</label>
        <select
          name="service"
          value={formData.service}
          onChange={handleChange}
          data-testid="contact-service"
          className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors bg-white"
        >
          <option value="">Select a service</option>
          <option value="transport">Transport & Logistics (HayalogiX)</option>
          <option value="equipment">Heavy Equipment (Bright Perfect)</option>
          <option value="parts">Spare Parts (MAK Spare Parts)</option>
          <option value="other">Other / General Inquiry</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={4}
          data-testid="contact-message"
          className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors resize-none"
          placeholder="Tell us about your requirements..."
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit"
        className="w-full bg-[#F5B800] text-black hover:bg-[#E5AB00] font-bold py-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? "Sending..." : "Send Enquiry"}
        {!isSubmitting && <Send size={18} />}
      </button>
    </form>
  );
};

export const Companies = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("mak-spare");

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && companies.find(c => c.id === hash)) {
      setActiveTab(hash);
    }
    if (hash === 'contact') {
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gray-900 py-20 md:py-28" data-testid="companies-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#F5B800] font-semibold text-sm uppercase tracking-wider mb-4 block">
              Our Companies
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Three Divisions, One Complete Solution
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              Discover our specialized companies working together to provide comprehensive heavy equipment, transport, and spare parts solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Companies Tabs */}
      <section className="py-16 md:py-24" data-testid="companies-tabs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full h-auto bg-transparent p-0 rounded-none flex flex-col md:flex-row mb-12 gap-4 md:gap-6">
              {companies.map((company) => (
                <TabsTrigger
                  key={company.id}
                  value={company.id}
                  data-testid={`tab-${company.id}`}
                  className="flex-1 py-4 px-4 rounded-none bg-transparent border-b-4 border-transparent data-[state=active]:border-[#F5B800] data-[state=active]:bg-transparent hover:opacity-80 transition-all cursor-pointer"
                >
                  {company.logo ? (
                    <img 
                      src={company.logo} 
                      alt={company.name}
                      className="h-16 md:h-20 w-auto object-contain mx-auto"
                    />
                  ) : (
                    <div className="h-16 md:h-20 flex items-center justify-center">
                      <span className="font-heading text-xl md:text-2xl font-bold text-gray-800">{company.name}</span>
                    </div>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {companies.map((company) => (
              <TabsContent key={company.id} value={company.id} className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Company Info */}
                    <div>
                      <div className="mb-8">
                        {company.logo ? (
                          <img 
                            src={company.logo} 
                            alt={company.name}
                            className="h-20 w-auto object-contain mb-4"
                          />
                        ) : (
                          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            {company.name}
                          </h2>
                        )}
                        <span className="text-[#D4A000] font-medium text-base">
                          {company.tagline}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 text-base leading-relaxed mb-8">
                        {company.description}
                      </p>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-8">
                        {company.stats.map((stat, idx) => (
                          <div key={idx} className="bg-gray-50 p-4 text-center">
                            <div className="font-heading text-2xl font-bold text-[#F5B800]">{stat.value}</div>
                            <div className="text-xs text-gray-500">{stat.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Services */}
                      <div className="bg-gray-50 p-6">
                        <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">
                          Our Services
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {company.services.map((service, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Check size={16} className="text-[#F5B800]" />
                              <span className="text-gray-700 text-base">{service}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Company Image */}
                    <div>
                      <img 
                        src={company.image} 
                        alt={company.name}
                        className="w-full h-auto shadow-lg"
                      />
                    </div>
                  </div>
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-gray-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <span className="text-[#D4A000] font-semibold text-sm uppercase tracking-wider mb-2 block">
                Get in Touch
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Let's Discuss Your Requirements
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Ready to partner with MAK Group? Contact us today to discuss your heavy equipment, transport, or spare parts requirements. We endeavour to answer all enquiries within 24 hours on business days.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5B800] flex items-center justify-center flex-shrink-0">
                    <MapPin size={22} className="text-black" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Visit Us</h4>
                    <p className="text-gray-600">
                      Industrial Area, Dubai<br />
                      United Arab Emirates
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5B800] flex items-center justify-center flex-shrink-0">
                    <Phone size={22} className="text-black" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Call Us</h4>
                    <a href="tel:+971563419000" className="text-gray-600 hover:text-[#D4A000] transition-colors" data-testid="contact-phone-link">
                      +971 56 341 9000
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5B800] flex items-center justify-center flex-shrink-0">
                    <Mail size={22} className="text-black" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Email Us</h4>
                    <a href="mailto:info@makgroup.ae" className="text-gray-600 hover:text-[#D4A000] transition-colors" data-testid="contact-email-link">
                      info@makgroup.ae
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 shadow-lg">
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-6">
                Send Us an Enquiry
              </h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
